export const userQuery = (start = 0, limit = 10, baseFilter = {}, searchTerm = '', searchableFields = []) => {
    const needsFullName = searchableFields.some(f => f.field === 'full_name');

    // If there's no search term, use the fast approach
    if (!searchTerm.trim() || !searchableFields.length) {
        return [
            baseFilter,
            {
                $facet: {
                    results: [
                        { $sort: { "_id": -1 } },
                        { $skip: start },
                        { $limit: limit },

                        // Join country
                        {
                            $lookup: {
                                from: "countries",
                                localField: "country",
                                foreignField: "_id",
                                as: "country"
                            }
                        },
                        {
                            $unwind: {
                                path: "$country",
                                preserveNullAndEmptyArrays: true
                            }
                        },

                        { $project: { password: 0 } }
                    ],
                    totalCount: [
                        { $count: "total" }
                    ]
                }
            }
        ];
    }

    // When searching, we need to do lookups for accurate count
    const searchRegexMatch = {
        $match: {
            $or: searchableFields.map(field =>
                field.isNumeric
                    ? {
                        [field.field]: isNaN(Number(searchTerm)) ? -1 : Number(searchTerm)
                    } : {
                        [field.field]: {
                            $regex: searchTerm,
                            $options: 'i'
                        }
                    }
            )
        }
    };

    // Lightweight pipeline for counting
    const countPipeline = [
        // Only do lookups needed for search fields
        ...(searchableFields.some(f => f.field.startsWith('country.')) ? [
            {
                $lookup: {
                    from: "countries",
                    localField: "country",
                    foreignField: "_id",
                    as: "country"
                }
            },
            {
                $unwind: {
                    path: "$country",
                    preserveNullAndEmptyArrays: true
                }
            }
        ] : []),

        // Add full_name for search if needed
        ...(needsFullName ? [{
            $addFields: {
                full_name: {
                    $concat: [
                        { $ifNull: ["$first_name", ""] },
                        " ",
                        { $ifNull: ["$last_name", ""] }
                    ]
                }
            }
        }] : []),

        // Apply search filter
        searchRegexMatch
    ];

    // Full pipeline for results
    const resultsPipeline = [
        ...countPipeline,

        // Sort and paginate first
        { $sort: { "createdAt": -1 } },
        { $skip: start },
        { $limit: limit },

        // Add remaining lookups that weren't needed for search
        ...(searchableFields.some(f => f.field.startsWith('country.')) ? [] : [
            {
                $lookup: {
                    from: "countries",
                    localField: "country",
                    foreignField: "_id",
                    as: "country"
                }
            },
            {
                $unwind: {
                    path: "$country",
                    preserveNullAndEmptyArrays: true
                }
            }
        ]),

        { $project: { password: 0 } }
    ];

    return [
        baseFilter,
        {
            $facet: {
                results: resultsPipeline,
                totalCount: [
                    ...countPipeline,
                    { $count: "total" }
                ]
            }
        }
    ];
};

export const transactionQuery = (start = 0, limit = 10, baseFilter = {}, searchTerm = '', searchableFields = []) => {
    const needsFullName = searchableFields.some(f => f.field === 'user.full_name');

    // If there's no search term, use the fast approach
    if (!searchTerm.trim() || !searchableFields.length) {
        return [
            baseFilter,
            {
                $facet: {
                    results: [
                        { $sort: { "paid_date.str": -1 } },
                        { $skip: start },
                        { $limit: limit },

                        { $lookup: { from: "transactions", localField: "transaction", foreignField: "_id", as: "transaction" } },
                        { $unwind: { path: "$transaction", preserveNullAndEmptyArrays: true } },

                        { $lookup: { from: "users", localField: "user", foreignField: "_id", as: "user" } },
                        { $unwind: { path: "$user", preserveNullAndEmptyArrays: true } },

                        { $lookup: { from: "properties", localField: "address.property", foreignField: "_id", as: "property" } },
                        { $unwind: { path: "$property", preserveNullAndEmptyArrays: true } },

                        { $lookup: { from: "products", localField: "product", foreignField: "_id", as: "product" } },
                        { $unwind: { path: "$product", preserveNullAndEmptyArrays: true } },

                        { $project: { password: 0 } }
                    ],
                    totalCount: [
                        { $count: "total" }
                    ]
                }
            }
        ];
    }

    // When searching, we need to do lookups for accurate count
    const searchRegexMatch = {
        $match: {
            $or: searchableFields.map(field =>
                field.isNumeric
                    ? {
                        [field.field]: isNaN(Number(searchTerm)) ? -1 : Number(searchTerm)
                    }
                    : {
                        [field.field]: {
                            $regex: searchTerm,
                            $options: 'i'
                        }
                    }
            )
        }
    };

    // Lightweight pipeline for counting (only essential lookups)
    const countPipeline = [
        // Only do lookups needed for search fields
        ...(searchableFields.some(f => f.field.startsWith('transaction.')) ? [
            { $lookup: { from: "transactions", localField: "transaction", foreignField: "_id", as: "transaction" } },
            { $unwind: { path: "$transaction", preserveNullAndEmptyArrays: true } }
        ] : []),

        ...(searchableFields.some(f => f.field.startsWith('user.')) ? [
            { $lookup: { from: "users", localField: "user", foreignField: "_id", as: "user" } },
            { $unwind: { path: "$user", preserveNullAndEmptyArrays: true } },

            ...(needsFullName ? [{
                $addFields: {
                    "user.full_name": {
                        $concat: [
                            { $ifNull: ["$user.first_name", ""] },
                            " ",
                            { $ifNull: ["$user.last_name", ""] }
                        ]
                    }
                }
            }] : [])
        ] : []),

        ...(searchableFields.some(f => f.field.startsWith('property.')) ? [
            { $lookup: { from: "properties", localField: "address.property", foreignField: "_id", as: "property" } },
            { $unwind: { path: "$property", preserveNullAndEmptyArrays: true } }
        ] : []),

        ...(searchableFields.some(f => f.field.startsWith('product.')) ? [
            { $lookup: { from: "products", localField: "product", foreignField: "_id", as: "product" } },
            { $unwind: { path: "$product", preserveNullAndEmptyArrays: true } }
        ] : []),

        // Apply search filter
        searchRegexMatch
    ];

    // Full pipeline for results (all lookups)
    const resultsPipeline = [
        ...countPipeline,

        // Sort and paginate first
        { $sort: { "paid_date.str": -1 } },
        { $skip: start },
        { $limit: limit },

        // Add remaining lookups that weren't needed for search
        ...(searchableFields.some(f => f.field.startsWith('transaction.')) ? [] : [
            { $lookup: { from: "transactions", localField: "transaction", foreignField: "_id", as: "transaction" } },
            { $unwind: { path: "$transaction", preserveNullAndEmptyArrays: true } }
        ]),

        ...(searchableFields.some(f => f.field.startsWith('user.')) ? [] : [
            { $lookup: { from: "users", localField: "user", foreignField: "_id", as: "user" } },
            { $unwind: { path: "$user", preserveNullAndEmptyArrays: true } }
        ]),

        ...(searchableFields.some(f => f.field.startsWith('property.')) ? [] : [
            { $lookup: { from: "properties", localField: "address.property", foreignField: "_id", as: "property" } },
            { $unwind: { path: "$property", preserveNullAndEmptyArrays: true } }
        ]),

        ...(searchableFields.some(f => f.field.startsWith('product.')) ? [] : [
            { $lookup: { from: "products", localField: "product", foreignField: "_id", as: "product" } },
            { $unwind: { path: "$product", preserveNullAndEmptyArrays: true } }
        ]),

        { $project: { password: 0 } }
    ];

    return [
        baseFilter,
        {
            $facet: {
                results: resultsPipeline,
                totalCount: [
                    ...countPipeline,
                    { $count: "total" }
                ]
            }
        }
    ];
};

export const wasteQuery = (start = 0, limit = 10, baseFilter = {}, searchTerm = '', searchableFields = []) => {
    const needsUserFullName = searchableFields.some(f => f.field === 'user.full_name');

    // If there's no search term, use the fast approach
    if (!searchTerm.trim() || !searchableFields.length) {
        return [
            baseFilter,
            {
                $facet: {
                    results: [
                        { $sort: { "_id": -1 } },
                        { $skip: start },
                        { $limit: limit },

                        // Lookups
                        {
                            $lookup: {
                                from: "collectors",
                                localField: "collector",
                                foreignField: "_id",
                                as: "collector"
                            }
                        },
                        { $unwind: { path: "$collector", preserveNullAndEmptyArrays: true } },

                        {
                            $lookup: {
                                from: "countries",
                                localField: "collector.country",
                                foreignField: "_id",
                                as: "collector.country"
                            }
                        },
                        { $unwind: { path: "$collector.country", preserveNullAndEmptyArrays: true } },

                        {
                            $lookup: {
                                from: "users",
                                localField: "user",
                                foreignField: "_id",
                                as: "user"
                            }
                        },
                        { $unwind: { path: "$user", preserveNullAndEmptyArrays: true } },

                        {
                            $lookup: {
                                from: "products",
                                localField: "product",
                                foreignField: "_id",
                                as: "product"
                            }
                        },
                        { $unwind: { path: "$product", preserveNullAndEmptyArrays: true } },

                        {
                            $lookup: {
                                from: "properties",
                                localField: "address.property",
                                foreignField: "_id",
                                as: "property"
                            }
                        },
                        { $unwind: { path: "$property", preserveNullAndEmptyArrays: true } },

                        { $project: { password: 0 } }
                    ],
                    totalCount: [
                        { $count: "total" }
                    ]
                }
            }
        ];
    }

    // When searching, we need to do lookups for accurate count
    const searchRegexMatch = {
        $match: {
            $or: searchableFields.map(field =>
                field.isNumeric
                    ? {
                        [field.field]: isNaN(Number(searchTerm)) ? -1 : Number(searchTerm)
                    }
                    : {
                        [field.field]: {
                            $regex: searchTerm,
                            $options: 'i'
                        }
                    }
            )
        }
    };

    // Lightweight pipeline for counting (no heavy lookups)
    const countPipeline = [
        // Only do essential lookups needed for search fields
        ...(searchableFields.some(f => f.field.startsWith('user.')) ? [
            {
                $lookup: {
                    from: "users",
                    localField: "user",
                    foreignField: "_id",
                    as: "user"
                }
            },
            { $unwind: { path: "$user", preserveNullAndEmptyArrays: true } },

            // Add full_name to user for searching
            ...(needsUserFullName ? [{
                $addFields: {
                    "user.full_name": {
                        $concat: [
                            { $ifNull: ["$user.first_name", ""] },
                            " ",
                            { $ifNull: ["$user.last_name", ""] }
                        ]
                    }
                }
            }] : [])
        ] : []),

        // Add other minimal lookups only if needed for search
        ...(searchableFields.some(f => f.field.startsWith('collector.')) ? [
            {
                $lookup: {
                    from: "collectors",
                    localField: "collector",
                    foreignField: "_id",
                    as: "collector"
                }
            },
            { $unwind: { path: "$collector", preserveNullAndEmptyArrays: true } }
        ] : []),

        ...(searchableFields.some(f => f.field.startsWith('product.')) ? [
            {
                $lookup: {
                    from: "products",
                    localField: "product",
                    foreignField: "_id",
                    as: "product"
                }
            },
            { $unwind: { path: "$product", preserveNullAndEmptyArrays: true } }
        ] : []),

        ...(searchableFields.some(f => f.field.startsWith('property.')) ? [
            {
                $lookup: {
                    from: "properties",
                    localField: "address.property",
                    foreignField: "_id",
                    as: "property"
                }
            },
            { $unwind: { path: "$property", preserveNullAndEmptyArrays: true } }
        ] : []),

        // Apply search filter
        searchRegexMatch
    ];

    // Full pipeline for results (all lookups)
    const resultsPipeline = [
        ...countPipeline,

        // Sort and paginate first
        { $sort: { "_id": -1 } },
        { $skip: start },
        { $limit: limit },

        // Now do the remaining expensive lookups only for the limited results
        ...(searchableFields.some(f => f.field.startsWith('collector.country')) || !searchableFields.some(f => f.field.startsWith('collector.')) ? [
            // Only lookup country if we haven't already looked up collector, or if we need country specifically
            ...(searchableFields.some(f => f.field.startsWith('collector.')) ? [] : [
                {
                    $lookup: {
                        from: "collectors",
                        localField: "collector",
                        foreignField: "_id",
                        as: "collector"
                    }
                },
                { $unwind: { path: "$collector", preserveNullAndEmptyArrays: true } }
            ]),

            {
                $lookup: {
                    from: "countries",
                    localField: "collector.country",
                    foreignField: "_id",
                    as: "collector.country"
                }
            },
            { $unwind: { path: "$collector.country", preserveNullAndEmptyArrays: true } }
        ] : []),

        // Add remaining lookups that weren't needed for search
        ...(searchableFields.some(f => f.field.startsWith('user.')) ? [] : [
            {
                $lookup: {
                    from: "users",
                    localField: "user",
                    foreignField: "_id",
                    as: "user"
                }
            },
            { $unwind: { path: "$user", preserveNullAndEmptyArrays: true } }
        ]),

        ...(searchableFields.some(f => f.field.startsWith('product.')) ? [] : [
            {
                $lookup: {
                    from: "products",
                    localField: "product",
                    foreignField: "_id",
                    as: "product"
                }
            },
            { $unwind: { path: "$product", preserveNullAndEmptyArrays: true } }
        ]),

        ...(searchableFields.some(f => f.field.startsWith('property.')) ? [] : [
            {
                $lookup: {
                    from: "properties",
                    localField: "address.property",
                    foreignField: "_id",
                    as: "property"
                }
            },
            { $unwind: { path: "$property", preserveNullAndEmptyArrays: true } }
        ]),

        { $project: { password: 0 } }
    ];

    return [
        baseFilter,
        {
            $facet: {
                results: resultsPipeline,
                totalCount: [
                    ...countPipeline,
                    { $count: "total" }
                ]
            }
        }
    ];
};
